import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CREATE_PARAM, wantsCreate } from "../lib/activityCreate";

/**
 * Issue #79: `?new=1` means "open this screen's create dialog".
 *
 * A drop-in replacement for the `useState(false)` that every create dialog
 * on this app already owns — same `[open, setOpen]` shape, so the calling
 * page keeps its existing close/reset handler and nothing else moves.
 *
 * Why the page reads a param rather than the Dashboard driving it: the
 * alternative offered was to navigate and then synthesise a click on the
 * target's button, which works until the button is renamed, lazily
 * mounted, or disabled — and fails silently, leaving someone on a page
 * wondering why nothing opened. A param is a contract the target page
 * owns; if the dialog stops honouring it, that is a visible test failure
 * on the page that broke, not a dangling click in the caller.
 *
 * THE PARAM IS CONSUMED, not left in the URL. Reading it once into the
 * initial state and then stripping it (`replace`, so no history entry)
 * means the intent is spent: closing the dialog and switching tabs on
 * `/learn` — which rewrites the query string with the other params intact
 * (`LearningHub.jsx`) — cannot carry `new=1` back round and re-open a
 * dialog the player just dismissed. It also keeps a shared or bookmarked
 * URL honest: what you copy out of the address bar is the screen, not the
 * screen plus a dialog.
 */
export function useCreateDialog() {
  const [sp, setSp] = useSearchParams();
  // Lazy initial state: the param is read on the mount that the deep link
  // caused. Later renders never re-open — by then the param is gone, and a
  // dialog that re-asserts itself on every render cannot be closed.
  const [open, setOpen] = useState(() => wantsCreate(sp));

  useEffect(() => {
    if (!wantsCreate(sp)) return;
    const next = new URLSearchParams(sp);
    next.delete(CREATE_PARAM);
    setSp(next, { replace: true });
  }, [sp, setSp]);

  return [open, setOpen];
}
